import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PLAN_NAMES } from "../components/plan/PlanUI";
import { CheckCircle2, Loader2, ArrowRight, Crown } from "lucide-react";

// Volta do checkout do Mercado Pago. A liberação é do webhook — aqui a gente
// só re-consulta o backend até o plano aparecer ativo (sem mão humana).
export default function PagamentoSucesso() {
  const { user, refresh } = useAuth();
  const [uso, setUso] = useState(null);
  const [tries, setTries] = useState(0);

  useEffect(() => {
    let cancel = false;
    const check = async () => {
      try {
        await refresh();
        const { data } = await api.get("/me/uso");
        if (!cancel) setUso(data);
      } catch { /* deslogado — mostra o estado genérico */ }
    };
    check();
    // O webhook pode levar alguns segundos: re-checa até ~30s
    const t = setInterval(() => {
      setTries((n) => n + 1);
      check();
    }, 5000);
    return () => { cancel = true; clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const planoAtivo = uso && uso.plano !== "gratis" && uso.pagamento_status === "ativo";
  const aindaProcessando = uso && !planoAtivo && tries < 6;
  const renova = uso?.plano_expira_em ? new Date(uso.plano_expira_em).toLocaleDateString("pt-BR") : null;

  return (
    <div className="dark min-h-screen bg-background text-foreground flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 16, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        className="w-full max-w-md rounded-3xl border border-border bg-card card-depth p-8 text-center space-y-5"
        data-testid="pagamento-sucesso"
      >
        {planoAtivo ? (
          <>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 260, damping: 16, delay: 0.15 }}
              className="mx-auto inline-flex rounded-full bg-emerald-500/15 p-4"
            >
              <CheckCircle2 className="h-10 w-10 text-emerald-400" strokeWidth={1.5} />
            </motion.div>
            <div className="space-y-1.5">
              <h1 className="text-2xl font-bold tracking-tight">Pagamento recebido 🎉</h1>
              <p className="text-sm text-muted-foreground">
                Seu plano <strong className="text-violet-300 inline-flex items-center gap-1">
                  {uso.plano === "pro" && <Crown className="h-3.5 w-3.5 text-amber-400" />}
                  {PLAN_NAMES[uso.plano] || uso.plano}
                </strong> já está ativo — liberado automaticamente, sem precisar falar com ninguém.
              </p>
              {renova && <p className="text-xs text-muted-foreground">Renova em {renova}.</p>}
            </div>
          </>
        ) : (
          <>
            <div className="mx-auto inline-flex rounded-full bg-violet-500/15 p-4">
              {aindaProcessando || !uso
                ? <Loader2 className="h-10 w-10 text-violet-400 animate-spin" strokeWidth={1.5} />
                : <CheckCircle2 className="h-10 w-10 text-violet-400" strokeWidth={1.5} />}
            </div>
            <div className="space-y-1.5">
              <h1 className="text-2xl font-bold tracking-tight">
                {user ? "Confirmando seu pagamento..." : "Pagamento em processamento"}
              </h1>
              <p className="text-sm text-muted-foreground">
                O Mercado Pago está avisando a gente. Assim que aprovar, seu plano é liberado
                sozinho — normalmente leva poucos segundos. Pode fechar essa tela tranquilo.
              </p>
            </div>
          </>
        )}
        <Link
          to="/"
          data-testid="pagamento-voltar"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-violet-600 hover:bg-violet-500 text-white font-medium px-6 py-3 text-sm transition glow-violet"
        >
          Ir pro app <ArrowRight className="h-4 w-4" />
        </Link>
      </motion.div>
    </div>
  );
}
